import {
  Drawer as MantineDrawer,
  type DrawerProps as MantineDrawerProps,
} from '@mantine/core';
import { DRAWER_CONTENT_ZINDEX, DRAWER_OVERLAY_ZINDEX } from '@zorro/types';
import { CSSProperties, ReactNode, useState } from 'react';

import { CloseButton } from '../CloseButton';
import styles from './Drawer.module.css';
import { DrawerFooterContext } from './_drawerFooterContext';

export { useDrawerFooter } from './_drawerFooterContext';

export type DrawerSize = 'sm' | 'md' | 'lg';

type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';

const SIZE_MAP: Record<DrawerSize, number> = {
  sm: 480,
  md: 640,
  lg: 780,
};

/**
 * Position the content element directly via position: fixed.
 * This bypasses the inner element positioning entirely, which is unreliable
 * in unstyled mode because Mantine's CSS variables don't cascade through
 * the portal. The Transition component's transform still animates correctly
 * on top of fixed positioning.
 */
const POSITION_STYLES: Record<DrawerPosition, CSSProperties> = {
  right: { top: 0, right: 0, bottom: 0 },
  left: { top: 0, left: 0, bottom: 0 },
  top: { top: 0, left: 0, right: 0 },
  bottom: { bottom: 0, left: 0, right: 0 },
};

export type DrawerProps = Omit<
  MantineDrawerProps,
  'variant' | 'color' | 'classNames' | 'style' | 'size'
> & {
  /** Size variant or custom CSS value (e.g. '20rem', '320px') controlling drawer width */
  size?: DrawerSize | (string & {});
  /** Background color override for the drawer panel (e.g. a brand token string) */
  bg?: string;
  /** Footer content (action buttons), pinned to the bottom */
  footer?: ReactNode;
  /** Shadow above the pinned footer (default `true`) */
  withFooterShadow?: boolean;
  /** Position of the close button: 'left' or 'right' */
  closeButtonPosition?: 'left' | 'right';
  /** Title variant; 'sticky' makes the header sticky on scroll */
  titleVariant?: 'sticky';
};

export function Drawer({
  opened,
  onClose,
  size = 'lg',
  bg,
  title,
  children,
  footer,
  withFooterShadow = true,
  position = 'right',
  withCloseButton = true,
  closeOnClickOutside,
  closeOnEscape,
  closeButtonPosition = 'right',
  titleVariant,
  className,
  ...props
}: DrawerProps) {
  const isHorizontal = position === 'left' || position === 'right';
  const sizeValue =
    (size as string) in SIZE_MAP
      ? SIZE_MAP[size as DrawerSize]
      : (size as string);

  const [contextFooter, setContextFooter] = useState<ReactNode>(null);
  const resolvedFooter = footer ?? contextFooter;

  return (
    <DrawerFooterContext.Provider value={setContextFooter}>
      <MantineDrawer.Root
        opened={opened}
        onClose={onClose}
        position={position}
        size={sizeValue}
        closeOnClickOutside={closeOnClickOutside}
        closeOnEscape={closeOnEscape}
        classNames={{
          content: styles.content,
          overlay: styles.overlay,
          header: styles.header,
          body: styles.body,
          title: styles.title,
        }}
        unstyled
        {...props}
      >
        <MantineDrawer.Overlay style={{ zIndex: DRAWER_OVERLAY_ZINDEX }} />
        <MantineDrawer.Content
          className={className}
          style={{
            zIndex: DRAWER_CONTENT_ZINDEX,
            ...POSITION_STYLES[position],
            width: isHorizontal ? sizeValue : undefined,
            height: isHorizontal ? undefined : sizeValue,
            background: bg,
          }}
        >
          {(title || withCloseButton) && (
            <MantineDrawer.Header
              data-close-position={closeButtonPosition}
              data-title-variant={titleVariant}
            >
              {title && <MantineDrawer.Title>{title}</MantineDrawer.Title>}
              {withCloseButton && (
                <CloseButton onClick={onClose} aria-label="Close drawer" />
              )}
            </MantineDrawer.Header>
          )}
          <MantineDrawer.Body>{children}</MantineDrawer.Body>
          {resolvedFooter && (
            <div
              className={styles.footer}
              data-with-footer-shadow={withFooterShadow || undefined}
            >
              {resolvedFooter}
            </div>
          )}
        </MantineDrawer.Content>
      </MantineDrawer.Root>
    </DrawerFooterContext.Provider>
  );
}

Drawer.displayName = 'Drawer';
